import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { RendererProps } from "../FilePreviewModal";
import CustomScrollbars from "../../CustomScrollbars";
import SearchBar from "../SearchBar";
import { HighlightedText } from "../searchUtils";
import { useDomSearchNavigation } from "../useDomSearchNavigation";

const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;

const JsonRenderer: React.FC<RendererProps> = ({ blob }) => {
  const [pretty, setPretty] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState("");
  const contentRef = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        const slice =
          fileBlob.size > MAX_PREVIEW_BYTES
            ? fileBlob.slice(0, MAX_PREVIEW_BYTES)
            : fileBlob;
        const text = await slice.text();
        if (cancelled) return;
        setTruncated(fileBlob.size > MAX_PREVIEW_BYTES);
        try {
          const parsed = JSON.parse(text);
          setPretty(JSON.stringify(parsed, null, 2));
          setParseError(null);
        } catch (err) {
          setParseError(
            err instanceof Error ? err.message : "Failed to parse JSON",
          );
          setRawText(text);
        }
      } catch (err) {
        if (!cancelled) {
          setParseError(
            err instanceof Error ? err.message : "Failed to read file",
          );
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const content = pretty ?? rawText ?? "";

  const { total, current, gotoNext, gotoPrev } = useDomSearchNavigation(
    contentRef,
    [content, search],
  );

  if (pretty == null && parseError == null) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          matchCount={total}
          currentMatch={current}
          onPrev={gotoPrev}
          onNext={gotoNext}
        />
      </Box>
      {truncated && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          File is larger than 5 MB — showing only the first part.
        </Alert>
      )}
      {parseError && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Invalid JSON: {parseError}. Showing raw content.
        </Alert>
      )}
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
        <Box
          component="pre"
          ref={contentRef}
          sx={{
            m: 0,
            p: 2,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Liberation Mono", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            backgroundColor: "background.paper",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {search ? (
            <HighlightedText text={content} query={search} />
          ) : (
            content
          )}
        </Box>
      </CustomScrollbars>
    </Box>
  );
};

export default JsonRenderer;
